/**
 * CreditLevelsSection Component
 * BRONZE/SILVER/GOLD/PLATINUM tiers with:
 * - Draggable score range bar (4 handles: bronze-start + 3 walls)
 * - Credit min/max as dual range slider
 * - Term checkboxes (4, 8, 12, 24 weeks)
 * - Info modals for credit formula and score_factor
 */

import { useState, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Info, HelpCircle, BarChart3 } from 'lucide-react';
import { SimulateImpactModal } from '../SimulateImpactModal';
import { simulateConfigImpact } from '@/services/credit-engine.service';
import { useToast } from '@/hooks';
import { PARTNER_ID } from '@/constants';
import type { CreditLevelItem, InterestRateItem } from '@/types/policy.types';

interface CreditLevelsSectionProps {
  data: CreditLevelItem[] | null;
  /**
   * Rates activos de Tasas de Interes — fuente unica de verdad de "que plazos existen".
   * Los chips por nivel se derivan de aqui, no de una lista hardcoded.
   * Si el hook aun no cargo rates, cae al fallback [4, 8, 12].
   */
  rates: InterestRateItem[] | null;
  canEdit: boolean;
  onSave: (data: CreditLevelItem[]) => void;
  isSaving: boolean;
}

// ─── Canonical level colors (matches CreditLevelBadge) ──────────────────────

interface LevelTheme {
  text: string;
  bg: string;
  border: string;
  color: string;
  label: string;
  description: string;
}

const LEVEL_THEMES: Record<string, LevelTheme> = {
  BRONZE: {
    text: 'text-[#CD7F32]', bg: 'bg-[rgba(205,127,50,0.12)]',
    border: 'border-l-[#CD7F32]', color: '#CD7F32', label: 'Bronze',
    description: 'Clientes nuevos con ingreso estable. Monto bajo, plazo corto.',
  },
  SILVER: {
    text: 'text-[#94A3B8]', bg: 'bg-[rgba(148,163,184,0.12)]',
    border: 'border-l-[#94A3B8]', color: '#94A3B8', label: 'Silver',
    description: 'Historial verificado. Montos medios, plazos flexibles.',
  },
  GOLD: {
    text: 'text-[#DAA520]', bg: 'bg-[rgba(218,165,32,0.12)]',
    border: 'border-l-[#DAA520]', color: '#DAA520', label: 'Gold',
    description: 'Alta capacidad comprobada. Montos altos, todos los plazos.',
  },
  PLATINUM: {
    text: 'text-[#3B82F6]', bg: 'bg-[rgba(59,130,246,0.12)]',
    border: 'border-l-[#3B82F6]', color: '#3B82F6', label: 'Platinum',
    description: 'Premium. Maximo score y antiguedad. Mejores condiciones.',
  },
};

const DEFAULT_THEME: LevelTheme = {
  text: 'text-muted-foreground', bg: 'bg-muted/10', border: 'border-l-muted',
  color: '#666', label: '?', description: '',
};

const DEFAULT_TERMS_FALLBACK = [4, 8, 12];
const SCORE_MAX = 100;
const MIN_RANGE = 5; // minimum points per level

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(amount);

// Credit slider constants
const CREDIT_STEP = 500;
const CREDIT_ABS_MIN = 500;

// ─── Component ──────────────────────────────────────────────────────────────

export function CreditLevelsSection({ data, rates, canEdit, onSave, isSaving }: CreditLevelsSectionProps) {
  const [draft, setDraft] = useState<CreditLevelItem[] | null>(null);
  const [showFormula, setShowFormula] = useState(false);
  const [showFactorHelp, setShowFactorHelp] = useState(false);
  const [simulationData, setSimulationData] = useState<any>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const scoreBarRef = useRef<HTMLDivElement>(null);
  const creditBarRefs = useRef<(HTMLDivElement | null)[]>([]);
  const toast = useToast();

  const sorted = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => a.display_order - b.display_order);
  }, [data]);

  // Fuente unica de verdad: los rates activos de Tasas de Interes.
  // Fallback a [4, 8, 12] mientras el hook carga (evita render vacio).
  const validTerms = useMemo(() => {
    if (!rates || rates.length === 0) return DEFAULT_TERMS_FALLBACK;
    return rates
      .filter(r => r.is_active)
      .map(r => r.term_weeks)
      .sort((a, b) => a - b);
  }, [rates]);

  const current = draft ?? sorted;

  // Dynamic credit slider max: highest max_credit_mxn + 25% headroom, rounded up to nearest 5000
  const creditAbsMax = useMemo(() => {
    if (current.length === 0) return CREDIT_ABS_MIN;
    const highest = Math.max(...current.map((l) => l.max_credit_mxn), CREDIT_ABS_MIN);
    return Math.ceil((highest * 1.25) / 5000) * 5000;
  }, [current]);

  if (sorted.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Sin niveles de credito configurados.
      </p>
    );
  }

  const isDirty = draft !== null;
  const lastMax = current[current.length - 1]?.max_score ?? SCORE_MAX;

  function ensureDraft(): CreditLevelItem[] {
    if (draft) return draft;
    const d = structuredClone(sorted);
    setDraft(d);
    return d;
  }

  // ─── Score bar: handles are at positions [0].min_score, [1].min_score, [2].min_score, [3].min_score
  // Handle 0 = left edge of Bronze, Handle 1-3 = walls between levels
  // lastMax (100) is fixed.

  function getHandlePositions(): number[] {
    return current.map((l) => l.min_score);
  }

  function moveHandle(handleIndex: number, newScore: number) {
    const base = ensureDraft();
    const updated = base.map((l) => ({ ...l }));
    const n = updated.length;

    // Clamp based on neighbors
    const leftLimit = handleIndex === 0 ? 0 : updated[handleIndex - 1]!.min_score + MIN_RANGE;
    const rightLimit = handleIndex < n - 1 ? updated[handleIndex + 1]!.min_score - MIN_RANGE : lastMax - MIN_RANGE;
    const clamped = Math.max(leftLimit, Math.min(rightLimit, newScore));

    // Update this level's min_score
    updated[handleIndex]!.min_score = clamped;

    // Update previous level's max_score (if exists)
    if (handleIndex > 0) {
      updated[handleIndex - 1]!.max_score = clamped - 1;
    }

    setDraft(updated);
  }

  function startScoreDrag(handleIndex: number, e: React.MouseEvent | React.TouchEvent) {
    e.preventDefault();
    const bar = scoreBarRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();

    const onMove = (clientX: number) => {
      const pct = (clientX - rect.left) / rect.width;
      const score = Math.round(pct * SCORE_MAX);
      moveHandle(handleIndex, score);
    };

    if ('touches' in e) {
      const move = (ev: TouchEvent) => { ev.preventDefault(); if (ev.touches[0]) onMove(ev.touches[0].clientX); };
      const end = () => { document.removeEventListener('touchmove', move); document.removeEventListener('touchend', end); };
      document.addEventListener('touchmove', move, { passive: false });
      document.addEventListener('touchend', end);
    } else {
      const move = (ev: MouseEvent) => onMove(ev.clientX);
      const end = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', end); };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', end);
    }
  }

  // ─── Credit range slider ──────────────────────────────────────────────────

  function startCreditDrag(levelIndex: number, which: 'min' | 'max', e: React.MouseEvent | React.TouchEvent) {
    e.preventDefault();
    const bar = creditBarRefs.current[levelIndex];
    if (!bar) return;
    const rect = bar.getBoundingClientRect();

    const onMove = (clientX: number) => {
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const raw = CREDIT_ABS_MIN + pct * (creditAbsMax - CREDIT_ABS_MIN);
      const snapped = Math.round(raw / CREDIT_STEP) * CREDIT_STEP;
      const base = ensureDraft();
      const updated = base.map((l) => ({ ...l }));
      const level = updated[levelIndex]!;

      if (which === 'min') {
        level.min_credit_mxn = Math.min(snapped, level.max_credit_mxn - CREDIT_STEP);
      } else {
        level.max_credit_mxn = Math.max(snapped, level.min_credit_mxn + CREDIT_STEP);
      }
      setDraft(updated);
    };

    if ('touches' in e) {
      const move = (ev: TouchEvent) => { ev.preventDefault(); if (ev.touches[0]) onMove(ev.touches[0].clientX); };
      const end = () => { document.removeEventListener('touchmove', move); document.removeEventListener('touchend', end); };
      document.addEventListener('touchmove', move, { passive: false });
      document.addEventListener('touchend', end);
    } else {
      const move = (ev: MouseEvent) => onMove(ev.clientX);
      const end = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', end); };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', end);
    }
  }

  function updateField(index: number, field: keyof CreditLevelItem, value: string | number) {
    const base = ensureDraft();
    const updated = base.map((l) => ({ ...l }));
    const level = updated[index];
    if (level) {
      updated[index] = { ...level, [field]: value };
      setDraft(updated);
    }
  }

  function toggleTerm(index: number, term: number) {
    const base = ensureDraft();
    const updated = base.map((l) => ({ ...l }));
    const level = updated[index];
    if (!level) return;
    const terms = level.available_terms.split(',').map(Number).filter(Boolean);
    const newTerms = terms.includes(term)
      ? terms.filter((t) => t !== term)
      : [...terms, term].sort((a, b) => a - b);
    if (newTerms.length === 0) return;
    updated[index] = { ...level, available_terms: newTerms.join(',') };
    setDraft(updated);
  }

  async function handleSimulate() {
    if (!draft) return;
    setIsSimulating(true);
    try {
      const res = await simulateConfigImpact(PARTNER_ID, 'credit-levels', draft);
      const impact = res.data;
      if (impact?.error === 'NO_PROFILES') {
        toast.error('No hay perfiles financieros para simular. Verifica que existan datos en CreditFinancialProfile_GT.');
        return;
      }
      setSimulationData(impact);
    } catch (err: any) {
      console.error('Simulate impact error:', err?.response?.data ?? err);
      toast.error('Error al simular impacto: ' + (err?.response?.data?.msg || err?.message || 'Error desconocido'));
    } finally {
      setIsSimulating(false);
    }
  }

  function handleApplyAfterSimulation() {
    if (draft) onSave(draft);
    setSimulationData(null);
    setDraft(null);
  }

  const handles = getHandlePositions();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Cada nivel define el rango de score, montos de credito y plazos disponibles.
        </p>
        <button
          type="button"
          onClick={() => setShowFormula(true)}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-sky-400 transition-colors hover:bg-sky-500/15"
        >
          <Info size={14} />
          Como se calcula el monto
        </button>
      </div>

      {/* Score range bar */}
      <div className="rounded-lg border bg-card p-3">
        <div className="mb-1.5 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>0</span>
          <span>{canEdit ? 'Arrastra los divisores para ajustar rangos' : 'Rangos de score por nivel'}</span>
          <span>{SCORE_MAX}</span>
        </div>
        <div ref={scoreBarRef} className="relative h-10 w-full rounded-md bg-muted/30 select-none">
          {/* Level segments */}
          {current.map((level) => {
            const theme = LEVEL_THEMES[level.level_name] ?? DEFAULT_THEME;
            const left = (level.min_score / SCORE_MAX) * 100;
            const width = ((level.max_score - level.min_score + 1) / SCORE_MAX) * 100;
            return (
              <div
                key={level.sk}
                className="absolute top-0 h-full flex flex-col items-center justify-center"
                style={{
                  left: `${left}%`,
                  width: `${width}%`,
                  backgroundColor: `${theme.color}20`,
                }}
              >
                <span className="text-[10px] font-bold truncate px-0.5" style={{ color: theme.color }}>
                  {theme.label}
                </span>
                <span className="text-[9px] font-medium" style={{ color: `${theme.color}99` }}>
                  {level.min_score}–{level.max_score}
                </span>
              </div>
            );
          })}

          {/* Draggable handles */}
          {canEdit && handles.map((score, i) => {
            const leftPct = (score / SCORE_MAX) * 100;
            return (
              <div
                key={`handle-${i}`}
                className="absolute top-0 z-10 flex h-full items-center justify-center"
                style={{ left: `${leftPct}%`, transform: 'translateX(-50%)', width: '20px', cursor: 'col-resize' }}
                onMouseDown={(e) => startScoreDrag(i, e)}
                onTouchStart={(e) => startScoreDrag(i, e)}
              >
                <div className="h-8 w-1.5 rounded-full bg-foreground/90 shadow-md transition-transform hover:scale-x-150" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Level cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {current.map((level, index) => {
          const theme = LEVEL_THEMES[level.level_name] ?? DEFAULT_THEME;
          const terms = level.available_terms.split(',').map(Number).filter(Boolean);
          const minPct = ((level.min_credit_mxn - CREDIT_ABS_MIN) / (creditAbsMax - CREDIT_ABS_MIN)) * 100;
          const maxPct = ((level.max_credit_mxn - CREDIT_ABS_MIN) / (creditAbsMax - CREDIT_ABS_MIN)) * 100;

          return (
            <div
              key={level.sk}
              className={`rounded-lg border bg-card p-3 border-l-[3px] ${theme.border} ${!level.is_active ? 'opacity-40' : ''}`}
            >
              {/* Level badge + score range */}
              <div className="mb-1 flex items-center justify-between">
                <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-bold uppercase ${theme.bg} ${theme.text}`}>
                  {theme.label}
                </span>
                <span className={`text-xs font-semibold ${theme.text}`}>
                  {level.min_score}–{level.max_score} pts
                </span>
              </div>

              {/* Description */}
              <p className="mb-2.5 text-[11px] leading-snug text-muted-foreground">
                {theme.description}
              </p>

              {/* Credit range slider */}
              <div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                  <span>Rango de credito</span>
                  <span className={`font-semibold ${theme.text}`}>
                    {formatCurrency(level.min_credit_mxn)} – {formatCurrency(level.max_credit_mxn)}
                  </span>
                </div>
                <div
                  ref={(el) => { creditBarRefs.current[index] = el; }}
                  className="relative h-5 w-full rounded bg-muted/30 select-none"
                >
                  {/* Filled range */}
                  <div
                    className="absolute top-0 h-full rounded"
                    style={{
                      left: `${minPct}%`,
                      width: `${maxPct - minPct}%`,
                      backgroundColor: `${theme.color}30`,
                    }}
                  />
                  {/* Min handle */}
                  {canEdit && (
                    <div
                      className="absolute top-0 z-10 flex h-full items-center justify-center"
                      style={{ left: `${minPct}%`, transform: 'translateX(-50%)', width: '16px', cursor: 'col-resize' }}
                      onMouseDown={(e) => startCreditDrag(index, 'min', e)}
                      onTouchStart={(e) => startCreditDrag(index, 'min', e)}
                    >
                      <div className="h-4 w-1.5 rounded-full shadow" style={{ backgroundColor: theme.color }} />
                    </div>
                  )}
                  {/* Max handle */}
                  {canEdit && (
                    <div
                      className="absolute top-0 z-10 flex h-full items-center justify-center"
                      style={{ left: `${maxPct}%`, transform: 'translateX(-50%)', width: '16px', cursor: 'col-resize' }}
                      onMouseDown={(e) => startCreditDrag(index, 'max', e)}
                      onTouchStart={(e) => startCreditDrag(index, 'max', e)}
                    >
                      <div className="h-4 w-1.5 rounded-full shadow" style={{ backgroundColor: theme.color }} />
                    </div>
                  )}
                </div>
              </div>

              {/* Terms */}
              <div className="mt-2.5">
                <span className="text-[11px] text-muted-foreground">Plazos (semanas)</span>
                <div className="mt-1 flex gap-1.5">
                  {validTerms.map((term) => {
                    const isActive = terms.includes(term);
                    return (
                      <button
                        key={term}
                        type="button"
                        disabled={!canEdit}
                        onClick={() => toggleTerm(index, term)}
                        className={`flex h-7 w-10 items-center justify-center rounded-md text-xs font-bold transition-colors ${
                          isActive
                            ? `${theme.bg} ${theme.text}`
                            : 'bg-muted/30 text-muted-foreground'
                        } ${canEdit ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                        style={isActive ? { boxShadow: `inset 0 0 0 1px ${theme.color}40` } : undefined}
                      >
                        {term}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Other fields */}
              <div className="mt-2.5 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Antiguedad min</span>
                  {canEdit ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        value={level.min_months}
                        onChange={(e) => updateField(index, 'min_months', Math.max(0, Number(e.target.value)))}
                        className={`h-6 w-14 rounded border border-transparent bg-muted/50 px-1.5 text-right text-xs font-semibold ${theme.text} focus:border-current focus:outline-none`}
                      />
                      <span className="text-[10px] text-muted-foreground">meses</span>
                    </div>
                  ) : (
                    <span className={`font-semibold ${theme.text}`}>{level.min_months} meses</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">Factor monto</span>
                    <button
                      type="button"
                      onClick={() => setShowFactorHelp(true)}
                      className="text-muted-foreground transition-colors hover:text-sky-400"
                      aria-label="Que es el factor monto"
                    >
                      <HelpCircle size={12} />
                    </button>
                  </div>
                  {canEdit ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        step="0.05"
                        min="0.05"
                        max="1"
                        value={level.score_factor}
                        onChange={(e) => updateField(index, 'score_factor', Number(e.target.value))}
                        className={`h-6 w-14 rounded border border-transparent bg-muted/50 px-1.5 text-right text-xs font-semibold ${theme.text} focus:border-current focus:outline-none`}
                      />
                      <span className="text-[10px] text-muted-foreground">= {((level.score_factor) * 100).toFixed(0)}%</span>
                    </div>
                  ) : (
                    <span className={`font-semibold ${theme.text}`}>{((level.score_factor) * 100).toFixed(0)}%</span>
                  )}
                </div>
                {/* Requisito de graduacion */}
                <div className="flex items-center justify-between text-xs" title="Para asignar este nivel el cliente debe tener un credito PAID_OFF sin mora del nivel seleccionado. Ej: PLATINUM requiere GOLD.">
                  <span className="text-muted-foreground">Requiere pagar</span>
                  {canEdit ? (
                    <select
                      value={level.requires_prior_completed_level ?? ''}
                      onChange={(e) => updateField(index, 'requires_prior_completed_level', e.target.value || '')}
                      className={`h-6 rounded border border-transparent bg-muted/50 px-1.5 text-right text-xs font-semibold ${theme.text} focus:border-current focus:outline-none`}
                    >
                      <option value="">Ninguno</option>
                      <option value="BRONZE">Bronze</option>
                      <option value="SILVER">Silver</option>
                      <option value="GOLD">Gold</option>
                    </select>
                  ) : (
                    <span className={`font-semibold ${theme.text}`}>
                      {level.requires_prior_completed_level
                        ? (LEVEL_THEMES[level.requires_prior_completed_level]?.label ?? level.requires_prior_completed_level)
                        : '—'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Save bar */}
      {isDirty && (
        <div className="sticky bottom-4 flex items-center justify-end gap-2 rounded-lg border bg-card p-2.5 shadow-lg">
          <span className="mr-auto text-xs text-muted-foreground">Cambios sin guardar</span>
          <Button variant="outline" size="sm" onClick={() => setDraft(null)}>
            Descartar
          </Button>
          <Button variant="primary" size="sm" leftIcon={<BarChart3 size={14} />} onClick={handleSimulate} isLoading={isSimulating}>
            {isSimulating ? 'Simulando...' : 'Simular y Guardar'}
          </Button>
        </div>
      )}

      {/* Factor help modal */}
      <Modal
        isOpen={showFactorHelp}
        onClose={() => setShowFactorHelp(false)}
        title="Que es el Factor Monto"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            El factor monto controla <strong className="text-foreground">que porcentaje de la capacidad de pago calculada</strong> se aprueba como credito. A mayor nivel, mayor factor, mayor monto aprobado.
          </p>

          <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-3">
            <p className="text-sm font-bold text-sky-400">monto_ajustado = capacidad_calculada × factor</p>
          </div>

          {/* Comparison table */}
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50 text-left text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Nivel</th>
                  <th className="px-3 py-2 font-medium">Factor</th>
                  <th className="px-3 py-2 font-medium">Capacidad $15,000</th>
                  <th className="px-3 py-2 font-medium">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { level: 'BRONZE', factor: 0.40, color: '#CD7F32' },
                  { level: 'SILVER', factor: 0.60, color: '#94A3B8' },
                  { level: 'GOLD', factor: 0.80, color: '#DAA520' },
                  { level: 'PLATINUM', factor: 1.00, color: '#3B82F6' },
                ].map((row) => (
                  <tr key={row.level} className="border-t">
                    <td className="px-3 py-2 font-bold" style={{ color: row.color }}>{row.level}</td>
                    <td className="px-3 py-2 font-semibold" style={{ color: row.color }}>{(row.factor * 100)}%</td>
                    <td className="px-3 py-2 text-muted-foreground">$15,000</td>
                    <td className="px-3 py-2 font-bold" style={{ color: row.color }}>{formatCurrency(15000 * row.factor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
              <p className="mb-1 text-xs font-bold text-emerald-400">Factor alto (1.0)</p>
              <p className="text-[11px] text-emerald-300/80">Mas credito aprobado. Mayor riesgo de sobreendeudamiento pero mejor experiencia para clientes premium.</p>
            </div>
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
              <p className="mb-1 text-xs font-bold text-amber-400">Factor bajo (0.4)</p>
              <p className="text-[11px] text-amber-300/80">Menos credito aprobado. Mas conservador. Adecuado para clientes nuevos con poco historial.</p>
            </div>
          </div>

          <div className="rounded-lg bg-muted/50 px-4 py-2.5">
            <p className="text-xs text-muted-foreground">
              <strong className="text-foreground">Tip:</strong> El factor se aplica ANTES del clamp min/max. Si el resultado es menor al credito minimo del nivel, se otorga el minimo. Si es mayor al maximo, se otorga el maximo.
            </p>
          </div>
        </div>
      </Modal>

      {/* Formula modal */}
      <Modal
        isOpen={showFormula}
        onClose={() => setShowFormula(false)}
        title="Como se calcula el monto maximo"
        size="md"
      >
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">
            El monto maximo de credito se basa en la capacidad de pago real del cliente, no solo su score.
          </p>

          <div className="space-y-0">
            {[
              { icon: '1', text: 'Calcular ingreso disponible: ingreso mensual × (1 - ratio de gastos) × 50%' },
              { icon: '2', text: 'Proyectar a 3 meses: disponible mensual × 3' },
              { icon: '3', text: 'Ajustar por factor del nivel: monto base × score_factor' },
              { icon: '4', text: 'Limitar al rango: CLAMP entre credito min y credito max del nivel' },
            ].map((step, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500/20 text-xs font-bold text-sky-400">
                    {step.icon}
                  </span>
                  {i < 3 && <div className="w-px flex-1 bg-border my-0.5" />}
                </div>
                <p className="pb-3 text-sm text-foreground">{step.text}</p>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-3 font-mono text-xs leading-relaxed text-sky-400">
            disponible = ingreso × (1 - gastos) × 0.5<br />
            monto_base = disponible × 3<br />
            monto_ajustado = monto_base × score_factor<br />
            <strong>max_credit = CLAMP(ajustado, min_credito, max_credito)</strong>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-[rgba(218,165,32,0.3)] bg-[rgba(218,165,32,0.08)] p-3">
              <p className="mb-1.5 text-xs font-bold text-[#DAA520]">GOLD — Ingreso $20,000</p>
              <div className="space-y-0.5 text-[11px] text-[#DAA520]/80">
                <p>Gasta 70% → disponible = $3,000</p>
                <p>Base = $3,000 × 3 = $9,000</p>
                <p>Ajustado = $9,000 × 0.80 = $7,200</p>
                <p className="font-bold">CLAMP → $8,000 (piso del nivel)</p>
              </div>
            </div>
            <div className="rounded-lg border border-[rgba(218,165,32,0.3)] bg-[rgba(218,165,32,0.08)] p-3">
              <p className="mb-1.5 text-xs font-bold text-[#DAA520]">GOLD — Ingreso $50,000</p>
              <div className="space-y-0.5 text-[11px] text-[#DAA520]/80">
                <p>Gasta 60% → disponible = $10,000</p>
                <p>Base = $10,000 × 3 = $30,000</p>
                <p>Ajustado = $30,000 × 0.80 = $24,000</p>
                <p className="font-bold">CLAMP → $14,000 (techo del nivel)</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-muted/50 px-4 py-2.5">
            <p className="text-xs text-muted-foreground">
              <strong className="text-foreground">Configuracion:</strong> Puedes modificar el score_factor (% de ajuste), y los montos min/max de cada nivel. El factor controla que tan agresivo es el calculo — a mayor factor, mayor monto aprobado.
            </p>
          </div>
        </div>
      </Modal>

      {simulationData && (
        <SimulateImpactModal
          data={simulationData}
          configType="credit-levels"
          onApply={handleApplyAfterSimulation}
          onCancel={() => setSimulationData(null)}
          isApplying={isSaving}
        />
      )}
    </div>
  );
}
